import React, { useCallback, useMemo } from 'react';
import { ReactSVG } from 'react-svg';
import { observer } from 'mobx-react-lite';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';

import './style.sass';

import closeIcon from 'assets/img/icons/close-icon.svg';
import { InputGroup } from '../common/InputGroup';
import { Button } from '../common/Button';
import { UserSettingsViewModel, userPersonalInfoValidationSchema } from 'app/viewModels/components/UserSettingsViewModel';
import { useModelWithArguments } from 'app/hooks/useViewModel';
import { UserPersonalInfoData } from 'src/types/user';

type Props = {
    userId: string,
    onClickCloseBtn: () => void,
};

export const UserSettings = observer(({ userId, onClickCloseBtn }: Props) => {
    const {
        email,
        name,
        submitPersonalInfoForm,
        resetPassword,
        isLoadingPersonalInfo,
        isLoadingReset,
    } = useModelWithArguments(UserSettingsViewModel, userId);

    const initialPersonalInfoValues = useMemo(() => ({
        name: name || '',
        email: email || '',
    }), [email, name]);

    const onSubmitForm = useCallback((data: UserPersonalInfoData, actions: FormikHelpers<UserPersonalInfoData>) => {
        const { resetForm } = actions;

        if (submitPersonalInfoForm) {
            submitPersonalInfoForm(data);
        }

        resetForm();

    }, [submitPersonalInfoForm]);

    return (
        <Formik
            initialValues={initialPersonalInfoValues}
            validationSchema={userPersonalInfoValidationSchema}
            onSubmit={onSubmitForm}
            enableReinitialize
        >
            {(({ values }: FormikProps<UserPersonalInfoData>) => (
                <Form>
                    <div className="user-settings">
                        <div className="user-settings__header">
                            <h2 className="title-h1 dark-violet">Account settings</h2>

                            <div className="user-settings__close-btn" onClick={onClickCloseBtn}>
                                <ReactSVG src={closeIcon}/>
                            </div>
                        </div>

                        <div className="user-settings__body">
                            <div className="user-settings__personal-info">
                                <div className="left-wrapper">
                                    <h3 className="p1-medium dark-violet">Personal information</h3>
                                    <span className="caption-text grey-1">Update your personal information</span>
                                </div>
                                <div className="right-wrapper">
                                    <InputGroup
                                        id="personalInfoName"
                                        name="name"
                                        autoComplete="name"
                                        placeholder="Enter your name"
                                        label="Name"
                                    />
                                    <InputGroup
                                        id="personalInfoEmail"
                                        name="email"
                                        autoComplete="email"
                                        placeholder="Enter email"
                                        label="Email"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="user-settings__footer">
                            <div className="user-settings__password-box">
                                <div className="left-wrapper">
                                    <h3 className="p1-medium dark-violet">Password</h3>
                                    <span className="caption-text grey-1">Enter your old password to update</span>
                                </div>
                                <div className="right-wrapper">
                                    <Button
                                        type="button"
                                        colorType="primary-light"
                                        size="big"
                                        text="Reset password"
                                        onClick={resetPassword}
                                        isLoading={isLoadingReset}
                                    />
                                </div>
                            </div>

                            <div className="user-settings__save-change">
                                <Button
                                    type='submit'
                                    size="big"
                                    text="Save change"
                                    disabled={values.email === email && values.name === name}
                                    isLoading={isLoadingPersonalInfo}
                                />
                            </div>
                        </div>
                    </div>
                </Form>
            ))}
        </Formik>
    );
});
